import { useCallback, useEffect, useMemo, useState } from 'react';
import { PARAMETROS_MEDICION_IFC_DEFAULT } from '@supernova/shared';
import type { PlanoGaleriaItem } from '../../api/planos.js';
import { getParametrosMedicionIfc } from '../../api/calibracion.js';

/**
 * Qué IFC de la galería toca dibujar fuera de pantalla para sacarle su vista previa (la foto la toma el visor 3D en el
 * navegador: el servidor no dibuja IFC). Lo usa la PÁGINA DEL PROYECTO, para que quien sube un IFC lo vea con su vista
 * previa sin tener que abrirlo. Uno a la vez, solo los que se abren sin pedir confirmación por tamaño (el tope de «IFC
 * grande» de Calibración) y solo quien puede escribir en el proyecto; cada uno se intenta una vez por visita.
 * Quien lo usa monta `MiniaturaIfcEnSegundoPlano` con `fileId` y llama `terminar` al acabar.
 */
export function useMiniaturasIfcEnSegundoPlano({
  activo,
  planos,
  tamanosBytes,
  recargar,
}: {
  activo: boolean;
  planos: readonly PlanoGaleriaItem[] | null | undefined;
  /** fileId → tamaño en bytes (null si no se sabe: entonces no se intenta). */
  tamanosBytes: ReadonlyMap<string, number | null>;
  recargar: () => Promise<void> | void;
}) {
  const [topeMb, setTopeMb] = useState<number | null>(null);
  const [intentadas, setIntentadas] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!activo || topeMb != null) return;
    void getParametrosMedicionIfc()
      .then((p) => setTopeMb(p.ifc_aviso_tamano_mb))
      .catch(() => setTopeMb(PARAMETROS_MEDICION_IFC_DEFAULT.ifc_aviso_tamano_mb));
  }, [activo, topeMb]);

  const fileId = useMemo(() => {
    if (!activo || topeMb == null) return null;
    const p = (planos ?? []).find((x) => {
      if (x.tipo !== 'ifc' || x.miniatura === 'ready' || intentadas.has(x.fileId)) return false;
      const bytes = tamanosBytes.get(x.fileId);
      if (bytes == null) return false;
      return bytes / (1024 * 1024) < topeMb;
    });
    return p?.fileId ?? null;
  }, [activo, topeMb, planos, tamanosBytes, intentadas]);

  const terminar = useCallback(
    (id: string, ok: boolean) => {
      setIntentadas((s) => new Set(s).add(id));
      if (ok) void recargar();
    },
    [recargar],
  );

  return { fileId, terminar };
}
